import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getTodayPuzzles, checkAttempt, getNextPuzzle } from '../services/api';
import { useAuth } from '../context/AuthContext';
import LogicForge from '../components/LogicForge';
import StoryForge from '../components/StoryForge';
import CipherForge from '../components/CipherForge';
import TriviaForge from '../components/TriviaForge';
import WordForge from '../components/WordForge';
import HistoryModal from '../components/HistoryModal';
import './Home.css';

const PUZZLE_TYPES = [
  { key: 'word',   label: 'Word',   icon: '📝', free: true  },
  { key: 'geo',    label: 'Story',  icon: '📖', free: false },
  { key: 'math',   label: 'Cipher', icon: '🔐', free: false },
  { key: 'trivia', label: 'Trivia', icon: '🧠', free: false },
  { key: 'logic',  label: 'Logic',  icon: '🧩', free: false },
];

const PUZZLE_PREVIEWS = {
  geo:    { title: 'StoryForge',  desc: 'Read the AI-generated story and fill in the missing word.', icon: '📖' },
  math:   { title: 'CipherForge', desc: 'Decode an encrypted message using letter substitution.', icon: '🔐' },
  trivia: { title: 'TriviaForge', desc: '5 questions, one topic, increasing difficulty.', icon: '🧠' },
  logic:  { title: 'LogicForge',  desc: 'Classic grid deduction puzzle. Match people to their roles.', icon: '🧩' },
};

function PremiumOverlay({ puzzle }) {
  const preview = PUZZLE_PREVIEWS[puzzle.key];
  return (
    <div className="premium-overlay">
      <div className="premium-overlay-card">
        <div className="premium-overlay-icon">{preview.icon}</div>
        <h2 className="mono">{preview.title}</h2>
        <p>{preview.desc}</p>
        <div className="premium-overlay-perks">
          <div className="perk mono">✦ 10 credits for $0.99</div>
          <div className="perk mono">✦ 100 credits for $5.99</div>
          <div className="perk mono">✦ Credits never expire</div>
          <div className="perk mono">✦ Works across all 5 games</div>
        </div>
        <Link to="/upgrade" className="btn-unlock mono">⚡ Get credits to unlock</Link>
        <Link to="/register" className="btn-free-signup mono">Create free account →</Link>
      </div>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="game-loading">
      <div style={{fontSize:'32px', marginBottom:'12px'}}>⏳</div>
      <div className="mono" style={{fontSize:'13px', color:'rgba(240,244,255,0.5)'}}>✨ Generating new puzzle powered by AI</div>
    </div>
  );
}

export default function Home() {
  const { user, credits, fetchCredits } = useAuth();
  const [puzzles, setPuzzles]         = useState([]);
  const [active, setActive]           = useState('word');
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [solved, setSolved]           = useState({});
  const [attempted, setAttempted]     = useState({});
  const [nextPuzzles, setNextPuzzles] = useState({});
  const [playingAgain, setPlayingAgain] = useState({});
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    const loadPuzzles = async () => {
      try {
        const r = await getTodayPuzzles();
        setPuzzles(r.data);
        if (user) {
          const checks = {};
          for (const p of r.data) {
            try {
              const res = await checkAttempt(p.id);
              if (res.data.already_attempted) {
                const pt = p.puzzle_type.toLowerCase();
                checks[pt] = true;
                if (res.data.solved) setSolved(s => ({ ...s, [pt]: true }));
              }
            } catch {}
          }
          setAttempted(checks);
        }
      } catch {
        setError("Could not load today's puzzles");
      } finally {
        setLoading(false);
      }
    };
    loadPuzzles();
  }, [user]);

  const getPuzzle   = (type) => nextPuzzles[type] || puzzles.find(p => p.puzzle_type === type.toUpperCase() || p.puzzle_type === type);
  const isPremium   = user?.subscription_status === 'pro' || (credits?.available_credits > 0);
  const activePuzzle = PUZZLE_TYPES.find(p => p.key === active);
  const isLocked    = !activePuzzle?.free && !isPremium;
  const theme       = getPuzzle('word')?.content?.theme;
  const themeEmoji  = getPuzzle('word')?.content?.theme_emoji;

  const handlePlayAgain = async (puzzleType) => {
    if (!credits?.available_credits || credits.available_credits <= 0) {
      window.location.href = '/upgrade';
      return;
    }
    setPlayingAgain(p => ({ ...p, [puzzleType]: true }));
    try {
      const res = await getNextPuzzle(puzzleType);
      setNextPuzzles(n => ({ ...n, [puzzleType]: res.data }));
      setAttempted(a => ({ ...a, [puzzleType]: false }));
      setSolved(s => ({ ...s, [puzzleType]: false }));
      if (fetchCredits) fetchCredits();
    } catch(e) {
      if (e?.response?.status === 402) {
        alert('Not enough credits. Get more to continue playing!');
        window.location.href = '/upgrade';
      } else {
        alert('Could not generate puzzle right now. Please try again in a moment.');
      }
    } finally {
      setPlayingAgain(p => ({ ...p, [puzzleType]: false }));
    }
  };

  const handleSolve = (puzzleType, solvedResult) => {
    setSolved(s => ({ ...s, [puzzleType]: solvedResult }));
    setAttempted(a => ({ ...a, [puzzleType]: true }));
    if (fetchCredits) fetchCredits();
  };

  if (loading) return (
    <div className="home-loading">
      <div className="loading-spinner" />
      <p className="mono">Generating today's puzzles...</p>
    </div>
  );

  if (error) return (
    <div className="home-error">
      <p>{error}</p>
      <button onClick={() => window.location.reload()} className="mono">Retry</button>
    </div>
  );

  const renderGame = (type) => {
    if (playingAgain[type]) return <LoadingSpinner />;
    const puzzle = getPuzzle(type);
    const commonProps = {
      key: puzzle?.id,
      puzzle,
      alreadyAttempted: attempted[type],
      onPlayAgain: playingAgain[type] ? null : () => handlePlayAgain(type),
      loadingPlayAgain: playingAgain[type],
    };
    switch(type) {
      case 'word':   return <WordForge   {...commonProps} onSolve={(s, g, sc, t) => handleSolve('word', s)} />;
      case 'geo':    return <StoryForge  {...commonProps} onSolve={(s) => handleSolve('geo', s)} />;
      case 'math':   return <CipherForge {...commonProps} onSolve={(s) => handleSolve('math', s)} />;
      case 'trivia': return <TriviaForge {...commonProps} onSolve={(s) => handleSolve('trivia', s)} />;
      case 'logic':  return <LogicForge  {...commonProps} onSolve={(s) => handleSolve('logic', s)} />;
      default: return null;
    }
  };

  const solvedCount = Object.values(solved).filter(Boolean).length;

  return (
    <div className="home">

      {/* ── Top info bar ── */}
      <div className="home-topbar">
        <div className="home-topbar-left">
          {theme ? (
            <span className="home-theme-pill mono">{themeEmoji} {theme} Week</span>
          ) : (
            <span className="home-theme-pill mono">🧩 Daily Puzzles</span>
          )}
          <span className="home-date mono">
            {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
          </span>
        </div>
        <div className="home-topbar-right">
          {user && <span className="home-streak mono">🔥 {user.current_streak || 0}</span>}
          <span className="home-progress mono">{solvedCount}/5</span>
          {user && (
            <button className="home-history-btn" onClick={() => setShowHistory(true)}>📋</button>
          )}
        </div>
      </div>

      {/* ── Game content ── */}
      <div className="home-content">
        {isLocked ? (
          <PremiumOverlay puzzle={activePuzzle} />
        ) : renderGame(active)}
      </div>

      {/* ── Bottom navigation ── */}
      <nav className="home-bottom-nav">
        {PUZZLE_TYPES.map(pt => {
          const available = pt.free || isPremium;
          const done = solved[pt.key];
          const isActive = active === pt.key;
          return (
            <button
              key={pt.key}
              className={`bottom-nav-item ${isActive ? 'active' : ''} ${done ? 'done' : ''}`}
              onClick={() => setActive(pt.key)}
            >
              <span className="bottom-nav-icon">
                {done ? '✅' : pt.icon}
                {!available && <span className="bottom-nav-lock">🔒</span>}
              </span>
              <span className="bottom-nav-label mono">{pt.label}</span>
              {isActive && <span className="bottom-nav-dot" />}
            </button>
          );
        })}
      </nav>

      {showHistory && <HistoryModal onClose={() => setShowHistory(false)} />}
    </div>
  );
}
